import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import type { MenuItemProps } from '@rio-cloud/rio-uikit/MenuItem';
import { useIntl } from 'react-intl';

import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { Tour } from '#domain/Tour.ts';

import { TourVehicleFleetMonitorLink } from './TourVehicleFleetMonitorLink.tsx';

interface TourDetailsSidebarFooterProps {
    tour: Tour;
    vehicleAssetId?: string;
    onEdit: () => void;
    onDuplicate: () => void;
    onSolveProblem: (tour: Tour) => void;
    onCancelTour: () => void;
    onDownloadArchive?: () => void;
    isDownloadingArchive?: boolean;
}

export const TourDetailsSidebarFooter = ({
    tour,
    vehicleAssetId,
    onEdit,
    onDuplicate,
    onSolveProblem,
    onCancelTour,
    onDownloadArchive,
    isDownloadingArchive = false,
}: TourDetailsSidebarFooterProps) => {
    const intl = useIntl();
    const showEditStartedTour = useFeatureToggle('showEditStartedTour', true);
    const fleetMonitorButton = vehicleAssetId ? <TourVehicleFleetMonitorLink vehicleAssetId={vehicleAssetId} /> : null;

    const duplicateTitle = (
        <span className='display-flex align-items-center gap-5' data-testid='tour-details-duplicate-button'>
            <span className='uikit-icon icon-duplicate' />
            {intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.single.title',
                defaultMessage: 'Duplicate tour',
            })}
        </span>
    );

    const editTitle = (
        <span className='display-flex align-items-center gap-5' data-testid='tour-details-edit-button'>
            <span className='uikit-icon icon-pencil' />
            {intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.edit.single.title',
                defaultMessage: 'Edit tour',
            })}
        </span>
    );

    const solveProblemTitle = (
        <span className='display-flex align-items-center gap-5'>
            <span className='uikit-icon icon-flash' />
            {intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.problem.single.title',
                defaultMessage: 'Resolve problem',
            })}
        </span>
    );

    const duplicateTourItem: MenuItemProps = {
        value: duplicateTitle,
        onSelect: onDuplicate,
    };

    const editTourItem: MenuItemProps = {
        value: editTitle,
        onSelect: onEdit,
    };

    const cancelTourItem: MenuItemProps = {
        value: (
            <span className='display-flex align-items-center gap-5'>
                <span className='uikit-icon icon-trash text-color-danger' />
                {intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.cancel.single.title',
                    defaultMessage: 'Cancel tour',
                })}
            </span>
        ),
        onSelect: onCancelTour,
    };
    const problemTourItems = showEditStartedTour ? [editTourItem, duplicateTourItem] : [duplicateTourItem];

    if (tour.status === 'problem') {
        return (
            <ButtonToolbar alignment={fleetMonitorButton ? 'space-between' : 'left'} className='width-100pct'>
                <ButtonDropdown
                    title={solveProblemTitle}
                    bsStyle='danger'
                    dropdownClassName='white-space-nowrap'
                    pullRight
                    splitButton
                    items={problemTourItems}
                    onLabelButtonClick={() => onSolveProblem(tour)}
                    data-testid='tour-details-button-dropdown-problem'
                    id='tour-details-button-dropdown-problem'
                />
                {fleetMonitorButton}
            </ButtonToolbar>
        );
    }

    if (tour.status === 'in-transit' && showEditStartedTour) {
        return (
            <ButtonToolbar alignment={fleetMonitorButton ? 'space-between' : 'left'} className='width-100pct'>
                <ButtonDropdown
                    title={editTitle}
                    bsStyle='primary'
                    dropdownClassName='white-space-nowrap'
                    pullRight
                    splitButton
                    items={[duplicateTourItem]}
                    onLabelButtonClick={onEdit}
                    id='tour-details-button-dropdown-active'
                />
                {fleetMonitorButton}
            </ButtonToolbar>
        );
    }

    if (['planned', 'open'].includes(tour.status)) {
        return (
            <ButtonToolbar alignment={fleetMonitorButton ? 'space-between' : 'left'} className='width-100pct'>
                <ButtonDropdown
                    title={editTitle}
                    bsStyle='primary'
                    dropdownClassName='white-space-nowrap'
                    pullRight
                    splitButton
                    items={[duplicateTourItem, cancelTourItem]}
                    onLabelButtonClick={onEdit}
                    id='tour-details-button-dropdown-cancel'
                />
                {fleetMonitorButton}
            </ButtonToolbar>
        );
    }

    if (tour.status === 'finished') {
        return (
            <ButtonToolbar alignment='space-between'>
                <Button
                    bsStyle='default'
                    iconName='duplicate'
                    autoTrackingKey='intl-msg:ordercom-cloud-frontend.tours.duplicate.single.title'
                    onClick={onDuplicate}
                    data-testid='tour-details-duplicate-button'
                >
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.single.title',

                        defaultMessage: 'Duplicate tour',
                    })}
                </Button>
                <Button
                    bsStyle={Button.PRIMARY}
                    iconName='download'
                    autoTrackingKey='ordercom.tours.details.archive.download'
                    onClick={onDownloadArchive}
                    disabled={isDownloadingArchive || !onDownloadArchive}
                    data-testid='tour-details-download-archive-button'
                >
                    {intl.formatMessage({
                        id: isDownloadingArchive
                            ? 'intl-msg:ordercom-cloud-frontend.tours.archive.running'
                            : 'intl-msg:ordercom-cloud-frontend.tours.archive.download',
                    })}
                </Button>
            </ButtonToolbar>
        );
    }

    return (
        <ButtonToolbar alignment={fleetMonitorButton ? 'space-between' : 'left'} className='width-100pct'>
            <Button
                bsStyle={Button.PRIMARY}
                iconName='duplicate'
                autoTrackingKey='intl-msg:ordercom-cloud-frontend.tours.duplicate.single.title'
                onClick={onDuplicate}
                data-testid='tour-details-duplicate-button'
            >
                {intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.single.title',

                    defaultMessage: 'Duplicate tour',
                })}
            </Button>
            {fleetMonitorButton}
        </ButtonToolbar>
    );
};
